import { lazy, Suspense, useRef, useState, useEffect } from 'react';

// Retry wrapper for lazy imports - handles chunk loading failures gracefully
const lazyWithRetry = (
  importFn: () => Promise<any>,
  namedExport?: string
): React.LazyExoticComponent<React.ComponentType<any>> => {
  return lazy(async () => {
    try {
      const module = await importFn();
      if (namedExport && namedExport in module) {
        return { default: module[namedExport] };
      }
      return module;
    } catch (error) {
      // On chunk load failure, try once more after a small delay
      console.warn('Chunk load failed, retrying...', error);
      await new Promise(resolve => setTimeout(resolve, 500));
      try {
        const module = await importFn();
        if (namedExport && namedExport in module) {
          return { default: module[namedExport] };
        }
        return module;
      } catch (retryError) {
        // If retry fails, trigger a page reload to get fresh assets
        console.error('Chunk load failed after retry, reloading...', retryError);
        window.location.reload();
        // Return empty component while reload happens
        return { default: () => null };
      }
    }
  });
};

// Lazy load heavy below-fold components with retry logic
export const LazyNetflixBrowse = lazyWithRetry(() => import('@/components/NetflixBrowse'), 'NetflixBrowse');
export const LazyCinemaCarousel = lazyWithRetry(() => import('@/components/CinemaCarousel'), 'CinemaCarousel');
export const LazyPermanentBundlesSection = lazyWithRetry(() => import('@/components/PermanentBundlesSection'), 'PermanentBundlesSection');

// Lazy load payment success animation (contains heavy Lottie player - ~85KB)
export const LazyPaymentSuccessAnimation = lazyWithRetry(() => import('@/components/PaymentSuccessAnimation'), 'PaymentSuccessAnimation');

// Skeleton placeholders with proper heights to prevent CLS
export const CinemaCarouselPlaceholder = () => (
  <div className="w-full min-h-[200px] md:min-h-[280px]">
    <div className="px-4 md:px-12">
      <div className="h-6 w-32 bg-muted/10 rounded mb-4 animate-pulse" />
      <div className="flex gap-3 overflow-hidden">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex-shrink-0 w-[120px] md:w-[180px] aspect-[2/3] bg-muted/10 rounded-xl animate-pulse" />
        ))}
      </div>
    </div>
  </div>
);

export const NetflixBrowsePlaceholder = () => (
  <div className="w-full min-h-[400px] space-y-8 px-4 md:px-12">
    {[1, 2].map((section) => (
      <div key={section}>
        <div className="h-6 w-40 bg-muted/10 rounded mb-4 animate-pulse" />
        <div className="flex gap-3 overflow-hidden">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="flex-shrink-0 w-[140px] md:w-[180px] aspect-[2/3] bg-muted/10 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    ))}
  </div>
);

// Minimal placeholder for generic use
const MinimalPlaceholder = () => (
  <div className="w-full h-48 animate-pulse bg-muted/10 rounded-xl" />
);

interface DeferredComponentProps {
  children: React.ReactNode;
  rootMargin?: string;
  placeholder?: React.ReactNode;
  minHeight?: string;
}

/**
 * Wrapper that only renders children when they're about to enter the viewport
 * Uses Intersection Observer for efficient detection
 */
export function DeferredRender({ 
  children, 
  rootMargin = '300px',
  placeholder = <MinimalPlaceholder />,
  minHeight
}: DeferredComponentProps) {
  const [shouldRender, setShouldRender] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // If already visible, render immediately
    const rect = element.getBoundingClientRect();
    if (rect.top < window.innerHeight + 300) {
      setShouldRender(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [rootMargin]);

  return (
    <div ref={ref} style={minHeight ? { minHeight } : undefined}>
      {shouldRender ? (
        <Suspense fallback={placeholder}>
          {children}
        </Suspense>
      ) : placeholder}
    </div>
  );
}
